// c3radar-nfl.js (Rails 8 / Turbo-friendly, ES module)
// Drop-in replacement for legacy c3radar-nfl.js

class C3RadarNFL {
  constructor() {
    this.data = {};
    this.dom_ready = false;

    // ENVIRONMENT DETECTION VARIABLES
    this.WINDOW_INNER_WIDTH = window.innerWidth;
    this.WINDOW_INNER_HEIGHT = window.innerHeight;
    this.PLATFORM = null;

    // TEST MAX VALUES (from legacy)
    this.SYMPTOM_SCORE_MAX = 132;
    this.GSC_MAX = 132;
    this.BESS_ERRORS_MAX = 30;

    this.IMMEDIATE_MEMORY_MAX = 30;
    this.DELAYED_RECALL_MAX = 10;
    this.CONCENTRATION_MAX = 5;
    this.ORIENTATION_MAX = 5;

    this.VOR_MAX = 16;

    // Symptoms, SAC-Immediate memory, SAC-Orientation, SAC Concentration, SAC Delayed Recall, GAIT Average time, BESS
    this.AXES_LABELS_ALL = [
      "Symptom Severity",
      "Balance",
      "Gait",
      "Immediate Memory",
      "Orientation",
      "Concentration",
      "Delayed Memory",
    ];
    this.AXES_DATA_NAMES_ALL = [
      "gsc",
      "bess_evaluation_errors",
      "tandem_avg_time",
      "immediate_memory",
      "orientation",
      "concentration",
      "delayed_recall",
    ];

    this.axes_labels = this.AXES_LABELS_ALL.slice();
    this.axes_data_names = this.AXES_DATA_NAMES_ALL.slice();

    this.month_names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    this.chart_colors = ["0,0,0,", "255,0,0,", "255,153,0,", "0,204,255,", "0,0,255,", "102,0,102,"];

    this.display_assessments = [];
    this.compare_index = 1;
    this.hot_spots = [];

    this.axes_radius = 0;
    this.data_radius = 0;

    this.CHART_OFFSET_L = 75;
    this.CHART_OFFSET_R = 60;
    this.LEGEND_OFFSET = 180;

    this.over_link = -1;
    this.is_initialized = false;
    this.has_baseline = false;

    // Turbo-friendly DOM ready
    window.addEventListener("turbo:load", () => {
      this.dom_ready = true;
    });

    // Also handle non-turbo pages just in case
    if (document.readyState === "interactive" || document.readyState === "complete") {
      this.dom_ready = true;
    } else {
      window.addEventListener("DOMContentLoaded", () => {
        this.dom_ready = true;
      });
    }
  }

  updateChart(json_data, chart_width, chart_height) {
    // In your C4Radar you stopped gating on dom_ready; keeping same behavior:
    chart_width = typeof chart_width !== "undefined" ? chart_width : null;
    chart_height = typeof chart_height !== "undefined" ? chart_height : null;
    this.loadData(json_data, chart_width, chart_height);
  }

  loadData(json_data, chart_width, chart_height) {
    this.data = json_data || {};
    const IPAD_W = 460;
    const IPAD_H = 460;

    this.hot_spots = [];
    this.display_assessments = [];

    if (chart_width == null || chart_height == null) {
      if (this.data.chart_width == null || this.data.chart_height == null) {
        this.data.chart_width = IPAD_W;
        this.data.chart_height = IPAD_H;
      }
    } else {
      this.data.chart_width = chart_width;
      this.data.chart_height = chart_height;
    }

    // Preserve legacy baseline/compare logic
    this.has_baseline = false;
    this.compare_index = 1;

    if (this.data.assessments) {
      if (this.data.assessments.length === 1) {
        this.compare_index = 0;
        if (this.data.assessments[0]?.baseline === true) {
          this.has_baseline = true;
        }
      } else if (this.data.assessments.length > 1) {
        if (this.data.assessments[1]?.baseline === true) {
          this.has_baseline = true;
        } else {
          this.has_baseline = false;
          this.compare_index = 0;
        }
      }
    } else {
      this.has_baseline = false;
      this.compare_index = 0;
    }

    // Reset axes for this run (important if countAxes shrinks them)
    this.axes_labels = this.AXES_LABELS_ALL.slice();
    this.axes_data_names = this.AXES_DATA_NAMES_ALL.slice();

    this.init();
  }

  detectUserAgent() {
    const ua = navigator.userAgent;
    if (ua.match(/iPad/i)) this.PLATFORM = "iPad";
    else if (ua.match(/iPod/i)) this.PLATFORM = "iPod";
    else if (ua.match(/iPhone/i)) this.PLATFORM = "iPhone";
    else if (ua.match(/Android/i)) this.PLATFORM = "Android";
    else if (ua.match(/Win/i)) this.PLATFORM = "Win";
    else if (ua.match(/Mac/i)) this.PLATFORM = "Mac";
    else this.PLATFORM = "UNKNOWN";
  }

  init() {
    this.detectUserAgent();

    // Guard: if we don’t have assessments, nothing meaningful to draw
    if (!this.data.assessments || this.data.assessments.length === 0) return;

    for (let i = 0; i < this.data.assessments.length; i++) {
      this.display_assessments.push(1);
    }

    if (this.has_baseline === true) {
      if (this.compare_index === 1) this.display_assessments[0] = 0;
      else this.display_assessments[1] = 0;
    }

    this.countAxes();

    let legend_h;
    if (this.PLATFORM === "iPad" || this.PLATFORM === "iPhone" || this.PLATFORM === "Android") {
      legend_h = this.data.assessments.length * 45;
    } else {
      legend_h = this.data.assessments.length * 20;
    }

    const max_diameter = Math.round(this.data.chart_width - this.CHART_OFFSET_L - this.CHART_OFFSET_R);
    const differential = max_diameter - (this.data.chart_height - legend_h);

    if (differential > legend_h * 0.8) {
      if (
        ((this.data.chart_width - this.LEGEND_OFFSET - this.CHART_OFFSET_L - this.CHART_OFFSET_R - 30) /
          this.data.chart_height) >
        1
      ) {
        this.axes_radius = Math.round((this.data.chart_height - 30) * 0.5);
      } else {
        this.axes_radius = Math.round(
          (this.data.chart_width - this.LEGEND_OFFSET - this.CHART_OFFSET_L - this.CHART_OFFSET_R - 60) * 0.5
        );
      }
    } else if (differential > 0) {
      this.axes_radius = Math.round(
        (this.data.chart_width - this.CHART_OFFSET_L - this.CHART_OFFSET_R - differential) * 0.5
      );
    } else {
      this.axes_radius = Math.round((this.data.chart_width - this.CHART_OFFSET_L - this.CHART_OFFSET_R) * 0.5);
    }

    this.data_radius = Math.round(this.axes_radius * 0.9);

    this.formatRadii();
    this.drawChart(
      this.axes_labels.length,
      this.data.chart_width,
      this.data.chart_height,
      this.axes_radius,
      this.data_radius
    );

    if (this.is_initialized === false) {
      this.setupEventListeners();
    }

    this.is_initialized = true;
  }

  countAxes() {
    let j = 0;
    const temp_labels = [];
    const temp_names = [];

    for (let i = 0; i < this.AXES_DATA_NAMES_ALL.length; i++) {
      const key = this.AXES_DATA_NAMES_ALL[i];
      const compare = this.data.assessments[this.compare_index]?.[key];

      if (compare != null && compare !== "no data") {
        temp_labels[j] = this.AXES_LABELS_ALL[i];
        temp_names[j] = key;
        j++;
      }
    }

    this.axes_labels = temp_labels;
    this.axes_data_names = temp_names;
  }

  formatRadii() {
    for (let i = 1 + Number(this.has_baseline); i < this.data.assessments.length; i++) {
      for (let k = 0; k < this.axes_labels.length; k++) {
        const name = this.axes_data_names[k];

        const assessment = this.data.assessments[i][name];
        const baseline = this.data.assessments[0 + this.compare_index][name];

        const setRadius = (val) => {
          this.data.assessments[i][`${name}_radius`] = val;
        };

        if (name === "c3_sss") {
          if (assessment == null || assessment === "no data") setRadius(0);
          else if (this.SYMPTOM_SCORE_MAX - baseline > (this.SYMPTOM_SCORE_MAX - assessment) * 2) setRadius(0);
          else {
            setRadius(
              Math.round((2 - (this.SYMPTOM_SCORE_MAX - baseline) / (this.SYMPTOM_SCORE_MAX - assessment)) * this.data_radius)
            );
          }
        } else if (name === "gsc") {
          if (assessment == null || assessment === "no data") setRadius(0);
          else if (this.GSC_MAX - baseline > (this.GSC_MAX - assessment) * 2) setRadius(0);
          else {
            setRadius(Math.round((2 - (this.GSC_MAX - baseline) / (this.GSC_MAX - assessment)) * this.data_radius));
          }
        } else if (name === "bess_evaluation_errors") {
          if (assessment == null || assessment === "no data") setRadius(0);
          else if (this.BESS_ERRORS_MAX - baseline > (this.BESS_ERRORS_MAX - assessment) * 2) setRadius(0);
          else {
            setRadius(
              Math.round((2 - (this.BESS_ERRORS_MAX - baseline) / (this.BESS_ERRORS_MAX - assessment)) * this.data_radius)
            );
          }
        } else if (name === "cap" || name === "maps") {
          if (assessment == null || assessment === "no data") setRadius(0);
          else if (baseline > assessment * 2) setRadius(0);
          else setRadius(Math.round((2 - baseline / assessment) * this.data_radius));
        } else if (name === "vor_dva") {
          if (assessment == null || assessment === "no data") setRadius(0);
          else setRadius(Math.round(((this.VOR_MAX - assessment) / (this.VOR_MAX - baseline)) * this.data_radius));
        } else if (name === "trails_diff") {
          // Kept as-is from your newer class version (sign-sensitive behavior)
          if (assessment == null || assessment === "no data") setRadius(0);
          else if (baseline < 0) {
            if (assessment < 0) setRadius(Math.round((baseline / assessment) * this.data_radius));
            else setRadius(0);
          } else if (baseline > 0) {
            if (assessment < 0) setRadius(Math.round(2 * this.data_radius));
            else if (assessment > 0) setRadius(Math.round((baseline / assessment) * this.data_radius));
            else setRadius(Math.round(2 * this.data_radius));
          } else {
            if (assessment < 0) setRadius(Math.round(2 * this.data_radius));
            else if (assessment > 0) setRadius(0);
            else setRadius(Math.round(1 * this.data_radius));
          }
        } else if (name === "tandem_avg_time") {
          // GAIT avg time: legacy had special math
          if (assessment == null || assessment === "no data") setRadius(0);
          else if (assessment > baseline * 2) setRadius(0);
          else {
            const m_rad = (assessment - baseline) / baseline;
            setRadius(Math.round(this.data_radius * (1 - m_rad)));
          }
        } else if (
          name === "immediate_memory" ||
          name === "orientation" ||
          name === "concentration" ||
          name === "delayed_recall"
        ) {
          let max_val = 0;
          switch (name) {
            case "immediate_memory":
              max_val = this.IMMEDIATE_MEMORY_MAX;
              break;
            case "orientation":
              max_val = this.ORIENTATION_MAX;
              break;
            case "concentration":
              max_val = this.CONCENTRATION_MAX;
              break;
            case "delayed_recall":
              max_val = this.DELAYED_RECALL_MAX;
              break;
          }

          if (assessment == null || assessment === "no data") setRadius(0);
          else if (baseline > assessment * 2) setRadius(0);
          else if (baseline === max_val && assessment === max_val) setRadius(this.data_radius);
          else {
            const base_val = baseline === max_val ? max_val : baseline;
            const post_val = assessment === max_val ? max_val : assessment;
            const ratio = (base_val - post_val) / base_val;
            setRadius(Math.round((1 - ratio) * this.data_radius));
          }
        } else {
          // Reaction time tests (fallback)
          if (assessment == null || assessment === "no data") setRadius(0);
          else if (assessment > baseline * 2) setRadius(0);
          else setRadius(Math.round((2 - assessment / baseline) * this.data_radius));
        }
      }
    }
  }

  drawChart(n, w, h, axes_radius, data_radius) {
    const canvas = document.getElementById("chart1");
    if (!canvas) return; // navigated away or missing element

    const context = canvas.getContext("2d");
    const format_assessment_data = [];

    // reset canvas (legacy trick)
    canvas.setAttribute("width", w - 1);
    canvas.setAttribute("height", h - 1);
    context.clearRect(0, 0, w, h);
    canvas.setAttribute("width", w);
    canvas.setAttribute("height", h);

    const chart_padding = parseInt(canvas.style.padding || "0", 10);
    const center_y = Math.round(axes_radius) + chart_padding + 2;

    let center_x;
    let legend_icon_size;
    let legend_icon_spacing;

    if (this.PLATFORM === "iPad" || this.PLATFORM === "iPhone" || this.PLATFORM === "Android") {
      center_x = Math.round(axes_radius + this.CHART_OFFSET_L);
      legend_icon_size = 20;
      legend_icon_spacing = 24;
    } else {
      center_x = Math.round(axes_radius + this.CHART_OFFSET_L);
      legend_icon_size = 10;
      legend_icon_spacing = 10;
    }

    // AXES
    context.fillStyle = "rgba(0,0,0,0.75)";
    context.strokeStyle = "rgba(0,0,0,0.5)";
    context.lineWidth = 1;
    context.lineCap = "butt";
    context.textBaseline = "bottom";
    context.textAlign = "left";
    context.font = "normal 12px sans-serif";

    for (let k = 0; k < n; k++) {
      format_assessment_data[k] = axes_radius;
    }
    this.drawPolygon(context, n, center_x, center_y, format_assessment_data, axes_radius, data_radius, 0, true);
    context.stroke();

    // NOTES
    context.fillStyle = `rgba(${this.chart_colors[0]}.5)`;
    context.textBaseline = "top";
    context.textAlign = "right";
    context.font = "normal 12px sans-serif";
    context.strokeStyle = `rgba(${this.chart_colors[0]}.3)`;
    context.lineWidth = 1;
    context.fillText("* Missing data.", w - 5, 4);
    context.fillText("\u2020 Truncated polygon/s.", w - 5, 20);
    context.fillStyle = `rgba(${this.chart_colors[0]}.05)`;
    context.fillRect(w - 134.5, 0.5, 134, 39);
    context.strokeRect(w - 134.5, 0.5, 134, 39);

    // ASSESSMENTS + LEGEND
    for (let j = 0; j < this.data.assessments.length; j++) {
      let i;
      if (j > 0) {
        if (this.has_baseline === true && j === 1) i = j;
        else i = this.data.assessments.length - j + Number(this.has_baseline);
      } else {
        i = j;
      }

      // legend box color
      if (i <= 0 + Number(this.has_baseline)) {
        context.fillStyle = `rgba(${this.chart_colors[0]}0.2)`;
        context.strokeStyle = `rgba(${this.chart_colors[0]}1)`;
      } else {
        context.fillStyle = `rgba(${this.chart_colors[i - Number(this.has_baseline)]}0.4)`;
        context.strokeStyle = `rgba(${this.chart_colors[i - Number(this.has_baseline)]}1)`;
      }

      context.lineWidth = 1;
      context.textBaseline = "bottom";
      context.textAlign = "left";
      context.font = "normal 12px sans-serif";

      context.fillRect(
        w - this.LEGEND_OFFSET,
        h - context.lineWidth - legend_icon_size - (legend_icon_size + legend_icon_spacing) * i,
        legend_icon_size,
        legend_icon_size
      );
      context.strokeRect(
        w - this.LEGEND_OFFSET,
        h - context.lineWidth - legend_icon_size - (legend_icon_size + legend_icon_spacing) * i,
        legend_icon_size,
        legend_icon_size
      );

      // legend text color
      if (i <= 0 + Number(this.has_baseline)) {
        context.fillStyle = `rgba(${this.chart_colors[0]}1)`;
      } else {
        context.fillStyle = `rgba(${this.chart_colors[i - Number(this.has_baseline)]}1)`;
      }

      context.fillText(
        this.data.assessments[i].description,
        w - this.LEGEND_OFFSET + legend_icon_size + Math.round(chart_padding * 0.5),
        h - (legend_icon_size + legend_icon_spacing) * i
      );

      // checkmark
      if (this.display_assessments[i] === 1) {
        if (this.PLATFORM === "iPad" || this.PLATFORM === "iPhone" || this.PLATFORM === "Android") {
          context.beginPath();
          context.lineWidth = 3;
          context.lineCap = "round";
          context.strokeStyle = "rgba(0, 0 ,0, 1)";
          context.moveTo(w - this.LEGEND_OFFSET + 2, h - Math.round(legend_icon_size * 0.5) - 1 - (legend_icon_size + legend_icon_spacing) * i);
          context.lineTo(w - 2 - this.LEGEND_OFFSET + Math.round(legend_icon_size * 0.5), h - 4 - (legend_icon_size + legend_icon_spacing) * i);
          context.lineTo(w - 2 - this.LEGEND_OFFSET + legend_icon_size, h - 6 - legend_icon_size - (legend_icon_size + legend_icon_spacing) * i);
          context.stroke();
        } else {
          context.beginPath();
          context.lineWidth = 2;
          context.strokeStyle = "rgba(0, 0 ,0, 1)";
          context.moveTo(w - this.LEGEND_OFFSET + 1, h - Math.round(legend_icon_size * 0.5) - 2 - (legend_icon_size + legend_icon_spacing) * i);
          context.lineTo(w - 1 - this.LEGEND_OFFSET + Math.round(legend_icon_size * 0.5), h - 3 - (legend_icon_size + legend_icon_spacing) * i);
          context.lineTo(w - 1 - this.LEGEND_OFFSET + legend_icon_size, h - 5 - legend_icon_size - (legend_icon_size + legend_icon_spacing) * i);
          context.stroke();
        }
      }

      // hotspots for legend clicks
      this.hot_spots.splice(1, 0, {
        id: i,
        description: this.data.assessments[i].description,
        left: w - this.LEGEND_OFFSET + chart_padding,
        top: h - legend_icon_size - legend_icon_spacing * 0.25 - (legend_icon_size + legend_icon_spacing) * i + chart_padding,
        width: this.LEGEND_OFFSET,
        height: legend_icon_size + legend_icon_spacing,
      });

      // polygon radii
      if (i <= 0 + Number(this.has_baseline)) {
        context.fillStyle = `rgba(${this.chart_colors[0]}0.2)`;
        context.strokeStyle = `rgba(${this.chart_colors[0]}1)`;
        for (let k = 0; k < n; k++) format_assessment_data[k] = data_radius;
      } else {
        context.fillStyle = `rgba(${this.chart_colors[i - Number(this.has_baseline)]}0.4)`;
        context.strokeStyle = `rgba(${this.chart_colors[i - Number(this.has_baseline)]}1)`;
        for (let k = 0; k < n; k++) {
          format_assessment_data[k] = this.data.assessments[i][`${this.axes_data_names[k]}_radius`];
        }
      }

      if (this.display_assessments[i] === 1) {
        context.lineWidth = 2;
        context.lineCap = "butt";
        context.beginPath();
        this.drawPolygon(context, n, center_x, center_y, format_assessment_data, axes_radius, data_radius, 0, false);
        context.fill();
        context.stroke();
      }
    }
  }

  drawPolygon(ctx, n, x, y, r, axes_radius, data_radius, angle, axes, counterclockwise) {
    angle = angle || 0;
    axes = axes || false;
    counterclockwise = counterclockwise || false;

    let i = 0;
    let temp_string = this.axes_labels[i];

    if (axes) {
      ctx.moveTo(x, y);
      ctx.lineTo(x + r[i] * Math.sin(angle), y - r[i] * Math.cos(angle));
      ctx.moveTo(x, y);

      for (let j = 1; j < this.data.assessments.length; j++) {
        if ((this.data.assessments[j][this.axes_data_names[i]] == null || this.data.assessments[j][this.axes_data_names[i]] === "no data") && this.display_assessments[j] === 1) {
          temp_string += " *";
          break;
        }
      }
      for (let j = 1; j < this.data.assessments.length; j++) {
        if (this.data.assessments[j][`${this.axes_data_names[i]}_radius`] > axes_radius && this.display_assessments[j] === 1) {
          temp_string += " \u2020";
          break;
        }
      }
      ctx.fillText(
        temp_string,
        x + r[i] * Math.sin(angle) - Math.round(ctx.measureText(temp_string).width * 0.5),
        y - r[i] * Math.cos(angle)
      );
    } else {
      if (r[i] > axes_radius) r[i] = axes_radius;
      ctx.moveTo(x + r[i] * Math.sin(angle), y - r[i] * Math.cos(angle));
    }

    const delta = (2 * Math.PI) / n;

    for (i = 1; i < n; i++) {
      angle += counterclockwise ? -delta : delta;
      temp_string = this.axes_labels[i];

      if (axes) {
        ctx.moveTo(x, y);
        ctx.lineTo(x + r[i] * Math.sin(angle), y - r[i] * Math.cos(angle));
        ctx.moveTo(x, y);

        ctx.textAlign = angle >= Math.PI ? "right" : "left";
        ctx.textBaseline = angle > Math.PI * 0.5 && angle < Math.PI * 1.5 ? "top" : "bottom";

        for (let j = 1; j < this.data.assessments.length; j++) {
          if ((this.data.assessments[j][this.axes_data_names[i]] == null || this.data.assessments[j][this.axes_data_names[i]] === "no data") && this.display_assessments[j] === 1) {
            temp_string += " *";
            break;
          }
        }
        for (let j = 1; j < this.data.assessments.length; j++) {
          if (this.data.assessments[j][`${this.axes_data_names[i]}_radius`] > axes_radius && this.display_assessments[j] === 1) {
            temp_string += " \u2020";
            break;
          }
        }

        ctx.fillText(temp_string, x + r[i] * Math.sin(angle), y - r[i] * Math.cos(angle));
      } else {
        if (r[i] > axes_radius) r[i] = axes_radius;
        ctx.lineTo(x + r[i] * Math.sin(angle), y - r[i] * Math.cos(angle));
      }
    }

    if (!axes) ctx.closePath();
  }

  setupEventListeners() {
    const canvas = document.getElementById("chart1");
    if (!canvas) return;

    const canvasLeft = canvas.offsetLeft;
    const canvasTop = canvas.offsetTop;

    let x;
    let y;

    canvas.addEventListener(
      "mousemove",
      (event) => {
        this.over_link = -1;

        if (typeof jQuery !== "undefined") {
          const targ = event.target || event.srcElement;
          x = event.pageX - $(targ).offset().left;
          y = event.pageY - $(targ).offset().top;
        } else {
          x = event.pageX - canvasLeft;
          y = event.pageY - canvasTop;
        }

        this.hot_spots.forEach((element) => {
          if (y > element.top && y < element.top + element.height && x > element.left && x < element.left + element.width) {
            this.over_link = element.id;
            if (this.over_link >= 0) document.body.style.cursor = "pointer";
          } else {
            if (this.over_link === -1) document.body.style.cursor = "";
          }
        });
      },
      false
    );

    canvas.addEventListener(
      "click",
      (event) => {
        if (this.over_link < 0) return;

        event.preventDefault();

        if (this.over_link <= 0 + Number(this.has_baseline)) {
          if (this.has_baseline === true) {
            this.display_assessments[this.over_link] = this.display_assessments[this.over_link] === 1 ? 0 : 1;
            this.display_assessments[Math.abs(this.over_link - 1)] =
              this.display_assessments[Math.abs(this.over_link - 1)] === 1 ? 0 : 1;

            this.compare_index = this.compare_index === 1 ? 0 : 1;

            // axes may change depending on compare assessment
            this.countAxes();
            this.formatRadii();
            this.drawChart(this.axes_labels.length, this.data.chart_width, this.data.chart_height, this.axes_radius, this.data_radius);
          }
        } else {
          this.display_assessments[this.over_link] = this.display_assessments[this.over_link] === 1 ? 0 : 1;
          this.drawChart(this.axes_labels.length, this.data.chart_width, this.data.chart_height, this.axes_radius, this.data_radius);
        }
      },
      false
    );
  }
}

export default C3RadarNFL;
